// WorksDashboard.js - чистый код с вынесенными состояниями
import React, { useState, useRef, useCallback } from 'react';
import { Card, Button, notification, Table, Input, Spin, Alert } from 'antd';
import { FilePdfOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { pdf, Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import {instance} from '../../../API/API'
import '../styles/worsanalytics.css';
import ReportHeader from "./components/ReportHeader";
import TripleValueDisplay from "./components/TripleValueDisplay";


// Регистрируем шрифты для поддержки кириллицы
Font.register({
    family: 'Roboto',
    fonts: [
        {
            src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf',
            fontWeight: 'normal',
        },
        {
            src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
            fontWeight: 'bold',
        },
    ],
});

// Стили для PDF
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#FFFFFF',
        padding: 10, // Уменьшили отступы
        fontFamily: 'Roboto',
        fontSize: 7, // Уменьшили размер шрифта
    },
    header: {
        marginBottom: 8, // Уменьшили отступ
        borderBottom: '1px solid #1890ff',
        paddingBottom: 5,
    },
    title: {
        fontSize: 14, // Уменьшили размер
        marginBottom: 3,
        color: '#1890ff',
        fontWeight: 'bold',
        textAlign: 'center',
    },
    subtitle: {
        fontSize: 10, // Уменьшили размер
        marginBottom: 3,
        color: '#666666',
        textAlign: 'center',
        fontWeight: 'bold',
    },
    metadataRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingHorizontal: 15,
    },
    metadata: {
        fontSize: 7, // Уменьшили размер
        color: '#999999',
        textAlign: 'center',
    },
    table: {
        display: 'table',
        width: 'auto',
        borderStyle: 'solid',
        borderWidth: 1,
        borderRightWidth: 0,
        borderBottomWidth: 0,
    },
    tableRow: {
        margin: 'auto',
        flexDirection: 'row',
        minHeight: 18, // Уменьшили высоту строк
    },
    tableColHeader: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        backgroundColor: '#f0f0f0',
        padding: 2, // Уменьшили отступы
        justifyContent: 'center',
        alignItems: 'center',
    },
    tableCol: {
        borderStyle: 'solid',
        borderWidth: 1,
        borderLeftWidth: 0,
        borderTopWidth: 0,
        padding: 1, // Уменьшили отступы
        justifyContent: 'center',
        alignItems: 'center',
    },
    tableCellHeader: {
        fontSize: 6, // Уменьшили размер шрифта
        fontWeight: 'bold',
        textAlign: 'center',
    },
    tableCell: {
        fontSize: 6, // Уменьшили размер шрифта
        textAlign: 'center',
    },
    footer: {
        position: 'absolute',
        bottom: 8,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: 6,
        color: '#999999',
    },
    pageNumber: {
        position: 'absolute',
        bottom: 3,
        left: 0,
        right: 0,
        textAlign: 'center',
        fontSize: 6,
        color: '#999999',
    },
});

// Компонент PDF документа
const MyDocument = ({ data, title, subtitle, period, generatedAt }) => (
    <Document>
        <Page size="A3" orientation="landscape" style={styles.page}>
            <View style={styles.header}>
                <Text style={styles.title}>{title}</Text>
                <Text style={styles.subtitle}>{subtitle}</Text>
                <View style={styles.metadataRow}>
                    <Text style={styles.metadata}>Период: {period}</Text>
                    <Text style={styles.metadata}>Сгенерировано: {generatedAt}</Text>
                </View>
            </View>

            <View style={styles.table}>
                <View style={styles.tableRow}>
                    <View style={[styles.tableColHeader, { width: '14%' }]}>
                        <Text style={styles.tableCellHeader}>Объект</Text>
                    </View>
                    <View style={[styles.tableColHeader, { width: '6%' }]}>
                        <Text style={styles.tableCellHeader}>Обсл. тек.</Text>
                    </View>
                    <View style={[styles.tableColHeader, { width: '6%' }]}>
                        <Text style={styles.tableCellHeader}>Обсл. прош.</Text>
                    </View>
                    <View style={[styles.tableColHeader, { width: '6%' }]}>
                        <Text style={styles.tableCellHeader}>Δ обсл.</Text>
                    </View>
                    <View style={[styles.tableColHeader, { width: '6%' }]}>
                        <Text style={styles.tableCellHeader}>Часы тек.</Text>
                    </View>
                    <View style={[styles.tableColHeader, { width: '6%' }]}>
                        <Text style={styles.tableCellHeader}>Часы прош.</Text>
                    </View>
                    <View style={[styles.tableColHeader, { width: '6%' }]}>
                        <Text style={styles.tableCellHeader}>Δ часов</Text>
                    </View>
                    <View style={[styles.tableColHeader, { width: '8%' }]}>
                        <Text style={styles.tableCellHeader}>Платежи тек. (€)</Text>
                    </View>
                    <View style={[styles.tableColHeader, { width: '8%' }]}>
                        <Text style={styles.tableCellHeader}>Платежи прош. (€)</Text>
                    </View>
                    <View style={[styles.tableColHeader, { width: '6%' }]}>
                        <Text style={styles.tableCellHeader}>Δ платежей</Text>
                    </View>
                    <View style={[styles.tableColHeader, { width: '8%' }]}>
                        <Text style={styles.tableCellHeader}>Стоим. часа тек. (€)</Text>
                    </View>
                    <View style={[styles.tableColHeader, { width: '8%' }]}>
                        <Text style={styles.tableCellHeader}>Стоим. часа прош. (€)</Text>
                    </View>
                    <View style={[styles.tableColHeader, { width: '6%' }]}>
                        <Text style={styles.tableCellHeader}>Δ стоим. часа</Text>
                    </View>
                </View>

                {data.map((row, index) => (
                    <View
                        style={[
                            styles.tableRow,
                            { backgroundColor: index % 2 === 0 ? '#fafafa' : 'white' }
                        ]}
                        key={row.objectId || index}
                    >
                        <View style={[styles.tableCol, { width: '14%' }]}>
                            <Text style={[styles.tableCell, { fontSize: 8 }]}>
                                {row.objectName || 'Не указан'}
                            </Text>
                        </View>
                        <View style={[styles.tableCol, { width: '6%' }]}>
                            <Text style={[styles.tableCell, { fontSize: 8 }]}>
                                {Number(row.services?.current || 0).toFixed(0)}
                            </Text>
                        </View>
                        <View style={[styles.tableCol, { width: '6%' }]}>
                            <Text style={[styles.tableCell, { fontSize: 8 }]}>
                                {Number(row.services?.last || 0).toFixed(0)}
                            </Text>
                        </View>
                        <View style={[styles.tableCol, { width: '6%' }]}>
                            <Text style={[
                                styles.tableCell,
                                getDynamicStyle(row.services?.dynamic),
                                { fontSize: 8 }
                            ]}>
                                {formatDynamicValue(row.services?.dynamic)}
                            </Text>
                        </View>
                        <View style={[styles.tableCol, { width: '6%' }]}>
                            <Text style={[styles.tableCell, { fontSize: 8 }]}>
                                {Number(row.manHours?.current || 0).toFixed(1)}
                            </Text>
                        </View>
                        <View style={[styles.tableCol, { width: '6%' }]}>
                            <Text style={[styles.tableCell, { fontSize: 8 }]}>
                                {Number(row.manHours?.last || 0).toFixed(1)}
                            </Text>
                        </View>
                        <View style={[styles.tableCol, { width: '6%' }]}>
                            <Text style={[
                                styles.tableCell,
                                getDynamicStyle(row.manHours?.dynamic),
                                { fontSize: 8 }
                            ]}>
                                {formatDynamicValue(row.manHours?.dynamic)}
                            </Text>
                        </View>
                        <View style={[styles.tableCol, { width: '8%' }]}>
                            <Text style={[styles.tableCell, { fontSize: 8 }]}>
                                €{Number(row.payments?.current || 0).toFixed(2)}
                            </Text>
                        </View>
                        <View style={[styles.tableCol, { width: '8%' }]}>
                            <Text style={[styles.tableCell, { fontSize: 8 }]}>
                                €{Number(row.payments?.last || 0).toFixed(2)}
                            </Text>
                        </View>
                        <View style={[styles.tableCol, { width: '6%' }]}>
                            <Text style={[
                                styles.tableCell,
                                getDynamicStyle(row.payments?.dynamic),
                                { fontSize: 8 }
                            ]}>
                                {formatDynamicValue(row.payments?.dynamic)}
                            </Text>
                        </View>
                        <View style={[styles.tableCol, { width: '8%' }]}>
                            <Text style={[styles.tableCell, { fontSize: 8 }]}>
                                €{Number(row.profitability?.current || 0).toFixed(2)}
                            </Text>
                        </View>
                        <View style={[styles.tableCol, { width: '8%' }]}>
                            <Text style={[styles.tableCell, { fontSize: 8 }]}>
                                €{Number(row.profitability?.last || 0).toFixed(2)}
                            </Text>
                        </View>
                        <View style={[styles.tableCol, { width: '6%' }]}>
                            <Text style={[
                                styles.tableCell,
                                getDynamicStyle(row.profitability?.dynamic),
                                { fontSize: 8 }
                            ]}>
                                {formatDynamicValue(row.profitability?.dynamic)}
                            </Text>
                        </View>
                    </View>
                ))}
            </View>

            <Text style={styles.footer}>
                Отчет сгенерирован автоматически. Данные актуальны на момент формирования отчета.
            </Text>
            <Text
                style={styles.pageNumber}
                render={({ pageNumber, totalPages }) => `Страница ${pageNumber} из ${totalPages}`}
                fixed
            />
        </Page>
    </Document>
);

// Вспомогательные функции для форматирования
const getDynamicStyle = (dynamic) => {
    const value = Number(dynamic || 0);
    if (value > 0) return { color: '#52c41a', fontWeight: 'bold' };
    if (value < 0) return { color: '#ff4d4f', fontWeight: 'bold' };
    return { color: '#666666' };
};

const formatDynamicValue = (dynamic) => {
    const value = Number(dynamic || 0);
    return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`;
};

const WorksDashboard = () => {
    const [exporting, setExporting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [sortedInfo, setSortedInfo] = useState({});
    const [tableData, setTableData] = useState({
        rawData: [],
        filteredData: [],
        displayData: []
    });

    // Загрузка данных
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await instance.get('/analytics/get_object_statistics.php', {
                params: {
                    startDateCurrent: dayjs().startOf('year').format('YYYY-MM-DD'),
                    endDateCurrent: dayjs().format('YYYY-MM-DD'),
                    startDateLast: dayjs().subtract(1, 'year').startOf('year').format('YYYY-MM-DD'),
                    endDateLast: dayjs().subtract(1, 'year').format('YYYY-MM-DD')
                },
                withCredentials: true
            });

            if (response.data.access) {
                const processedData = processData(response.data.data);
                const activeData = filterActiveObjects(processedData);
                setTableData({
                    rawData: processedData,
                    filteredData: activeData,
                    displayData: activeData
                });
            } else {
                throw new Error('Access denied');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    // Обработка данных
    const processData = (data) => {
        return data.map(item => {
            const profitabilityCurrent = item.manHours.current > 0 ?
                item.payments.current / item.manHours.current : 0;
            const profitabilityLast = item.manHours.last > 0 ?
                item.payments.last / item.manHours.last : 0;

            const calculateDynamic = (current, last) => {
                if (last === 0) return current > 0 ? 100 : 0;
                return ((current - last) / last) * 100;
            };

            return {
                ...item,
                profitability: {
                    current: profitabilityCurrent,
                    last: profitabilityLast,
                    dynamic: calculateDynamic(profitabilityCurrent, profitabilityLast)
                }
            };
        });
    };

    // Фильтрация активных объектов
    const filterActiveObjects = (data) => {
        return data.filter(item =>
            item.services.current > 0 ||
            item.services.last > 0 ||
            item.manHours.current > 0 ||
            item.manHours.last > 0
        );
    };

    // Поиск
    const handleSearch = (value) => {
        setSearchText(value);
        if (value) {
            const filtered = tableData.rawData.filter(item =>
                item.objectName.toLowerCase().includes(value.toLowerCase())
            );
            const activeFiltered = filterActiveObjects(filtered);
            setTableData(prev => ({
                ...prev,
                filteredData: activeFiltered,
                displayData: activeFiltered
            }));
        } else {
            const activeData = filterActiveObjects(tableData.rawData);
            setTableData(prev => ({
                ...prev,
                filteredData: activeData,
                displayData: activeData
            }));
        }
        setSortedInfo({});
    };

    // Сортировка
    // Замените текущую функцию handleTableChange на эту исправленную версию
    const handleTableChange = (pagination, filters, sorter) => {
        setSortedInfo(sorter);

        if (!sorter.field || !sorter.order) {
            setTableData(prev => ({
                ...prev,
                displayData: prev.filteredData
            }));
            return;
        }

        const sorted = [...tableData.filteredData].sort((a, b) => {
            let aValue, bValue;

            if (sorter.field === 'objectName') {
                aValue = a.objectName || '';
                bValue = b.objectName || '';
                return sorter.order === 'ascend'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
            } else {
                // Для вложенных полей (services, manHours, payments, profitability)
                const fieldMap = {
                    'services': 'services.current',
                    'manHours': 'manHours.current',
                    'payments': 'payments.current',
                    'profitability': 'profitability.current'
                };

                const fieldPath = fieldMap[sorter.field];
                if (fieldPath) {
                    const [parent, child] = fieldPath.split('.');
                    aValue = a[parent] ? a[parent][child] : 0;
                    bValue = b[parent] ? b[parent][child] : 0;
                } else {
                    // Прямое поле
                    aValue = a[sorter.field] || 0;
                    bValue = b[sorter.field] || 0;
                }

                // Для числовых значений
                aValue = Number(aValue);
                bValue = Number(bValue);

                return sorter.order === 'ascend' ? aValue - bValue : bValue - aValue;
            }
        });

        setTableData(prev => ({
            ...prev,
            displayData: sorted
        }));
    };

    // Обновление данных
    const handleRefresh = () => {
        fetchData();
        setSearchText('');
        setSortedInfo({});
    };

    // Экспорт в PDF
    const exportToPDF = async () => {
        setExporting(true);
        try {
            const dataToExport = tableData.displayData;

            if (dataToExport.length === 0) {
                notification.warning({
                    message: 'Нет данных',
                    description: 'Нет данных для экспорта'
                });
                return;
            }

            const currentYear = new Date().getFullYear();
            const period = `${currentYear} vs ${currentYear - 1}`;
            const generatedAt = new Date().toLocaleString('ru-RU');

            const blob = await pdf(
                <MyDocument
                    data={dataToExport}
                    title="Статистика обслуживания объектов"
                    subtitle="Анализ рентабельности и эффективности"
                    period={period}
                    generatedAt={generatedAt}
                />
            ).toBlob();

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `Статистика_обслуживания_объектов_A3_${currentYear}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            notification.success({
                message: 'PDF успешно создан',
                description: 'Отчет сохранен в формате A3'
            });

        } catch (error) {
            console.error('Ошибка при экспорте в PDF:', error);
            notification.error({
                message: 'Ошибка при создании PDF',
                description: 'Попробуйте еще раз или обратитесь к администратору'
            });
        } finally {
            setExporting(false);
        }
    };

    // Колонки таблицы
    // В определении columns, замените текущие колонки на эти:
    const columns = [
        {
            title: (
                <Input
                    placeholder="Поиск по объекту"
                    prefix={<SearchOutlined />}
                    value={searchText}
                    onChange={(e) => handleSearch(e.target.value)}
                    size="small"
                    allowClear
                />
            ),
            dataIndex: 'objectName',
            key: 'objectName',
            fixed: 'left',
            width: 250,
            sorter: true,
            sortOrder: sortedInfo.columnKey === 'objectName' ? sortedInfo.order : null,
            render: (name) => (
                <div style={{ fontWeight: 500, fontSize: '14px', color: '#262626' }}>
                    {name}
                </div>
            )
        },
        {
            title: 'Количество обслуживаний',
            key: 'services',
            dataIndex: 'services',
            width: 150,
            align: 'center',
            sorter: true,
            sortOrder: sortedInfo.columnKey === 'services' ? sortedInfo.order : null,
            render: (_, record) => (
                <TripleValueDisplay
                    current={record.services.current}
                    last={record.services.last}
                    dynamic={record.services.dynamic}
                    format="number"
                    size="small"
                />
            )
        },
        {
            title: 'Затрачено часов',
            key: 'manHours',
            dataIndex: 'manHours',
            width: 150,
            align: 'center',
            sorter: true,
            sortOrder: sortedInfo.columnKey === 'manHours' ? sortedInfo.order : null,
            render: (_, record) => (
                <TripleValueDisplay
                    current={record.manHours.current}
                    last={record.manHours.last}
                    dynamic={record.manHours.dynamic}
                    format="hours"
                    size="small"
                />
            )
        },
        {
            title: 'Поступившие платежи (€)',
            key: 'payments',
            dataIndex: 'payments',
            width: 150,
            align: 'center',
            sorter: true,
            sortOrder: sortedInfo.columnKey === 'payments' ? sortedInfo.order : null,
            render: (_, record) => (
                <TripleValueDisplay
                    current={record.payments.current}
                    last={record.payments.last}
                    dynamic={record.payments.dynamic}
                    format="currency"
                    size="small"
                />
            )
        },
        {
            title: 'Стоимость часа работы (€)',
            key: 'profitability',
            dataIndex: 'profitability',
            width: 160,
            align: 'center',
            sorter: true,
            sortOrder: sortedInfo.columnKey === 'profitability' ? sortedInfo.order : null,
            render: (_, record) => (
                <TripleValueDisplay
                    current={record.profitability.current}
                    last={record.profitability.last}
                    dynamic={record.profitability.dynamic}
                    format="currency"
                    size="small"
                />
            )
        },
    ];

    // Загрузка данных при монтировании
    React.useEffect(() => {
        fetchData();
    }, [fetchData]);

    return (
        <div className="dashboard">
            <div style={{
                background: 'white',
                padding: '20px',
                boxSizing: 'border-box'
            }}>
                <ReportHeader
                    title="Статистика обслуживания объектов"
                    subtitle="Анализ рентабельности и эффективности"
                    period={`${new Date().getFullYear()} vs ${new Date().getFullYear() - 1}`}
                    generatedAt={new Date().toLocaleString('ru-RU')}
                />

                <Card>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 16,
                        padding: '8px 0'
                    }}>
                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                            Детальная статистика по объектам
                        </h3>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={handleRefresh}
                            disabled={loading}
                        >
                            Обновить
                        </Button>
                    </div>

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '50px' }}>
                            <Spin size="large" />
                            <div style={{ marginTop: '16px' }}>Загрузка данных...</div>
                        </div>
                    ) : error ? (
                        <Alert
                            message="Ошибка загрузки данных"
                            description={error}
                            type="error"
                            showIcon
                            action={
                                <Button size="small" onClick={handleRefresh}>
                                    Повторить
                                </Button>
                            }
                        />
                    ) : (
                        <Table
                            columns={columns}
                            dataSource={tableData.displayData}
                            scroll={{ x: 800 }}
                            bordered
                            size="small"
                            pagination={false}
                            rowKey="objectId"
                            onChange={handleTableChange}
                            style={{
                                borderRadius: '8px',
                                overflow: 'hidden'
                            }}
                        />
                    )}
                </Card>
            </div>

            <div style={{
                position: 'sticky',
                bottom: 20,
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: 16,
                zIndex: 1000
            }}>
                <Button
                    type="primary"
                    icon={<FilePdfOutlined />}
                    onClick={exportToPDF}
                    loading={exporting}
                    size="large"
                    style={{
                        background: '#ff4d4f',
                        borderColor: '#ff4d4f',
                        boxShadow: '0 4px 8px rgba(255, 77, 79, 0.3)',
                        borderRadius: '6px'
                    }}
                >
                    Экспорт в PDF
                </Button>
            </div>
        </div>
    );
};

export default WorksDashboard;